import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Lock, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordModal({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function reset() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setSaving(false);
  }

  function close() {
    reset();
    onClose?.();
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("A nova palavra-passe deve ter no mínimo 8 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("A confirmação não corresponde à nova palavra-passe");
      return;
    }
    if (newPassword === currentPassword) {
      setError("A nova palavra-passe não pode ser igual à atual");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Palavra-passe alterada com sucesso");
      close();
    } catch (er) {
      const msg = formatApiErrorDetail(er.response?.data?.detail) || er.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="bg-white rounded-sm w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="change-password-modal"
      >
        <div className="border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
              Segurança
            </div>
            <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
              Alterar palavra-passe
            </h2>
          </div>
          <button
            data-testid="change-password-close"
            onClick={close}
            className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-8 space-y-5" data-testid="change-password-form">
          <PasswordField
            label="Palavra-passe atual *"
            value={currentPassword}
            onChange={setCurrentPassword}
            visible={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            testid="input-current-password"
          />
          <PasswordField
            label="Nova palavra-passe *"
            value={newPassword}
            onChange={setNewPassword}
            visible={showNew}
            onToggle={() => setShowNew((v) => !v)}
            testid="input-new-password"
            hint="Mínimo 8 caracteres"
          />
          <PasswordField
            label="Confirmar nova palavra-passe *"
            value={confirmPassword}
            onChange={setConfirmPassword}
            visible={showNew}
            onToggle={() => setShowNew((v) => !v)}
            testid="input-confirm-password"
          />

          {error && (
            <div
              data-testid="change-password-error"
              className="text-sm font-manrope text-[#FF4500] border border-[#FF4500]/20 bg-[#FF4500]/5 px-4 py-3 rounded-sm"
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-200">
            <button
              type="button"
              data-testid="change-password-cancel"
              onClick={close}
              className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              data-testid="change-password-submit"
              disabled={saving}
              className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm disabled:opacity-60 flex items-center gap-2"
            >
              <Lock className="w-4 h-4" strokeWidth={1.8} />
              {saving ? "A alterar…" : "Alterar palavra-passe"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, visible, onToggle, testid, hint }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          data-testid={testid}
          type={visible ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 pr-11 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] transition-all outline-none"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-900"
        >
          {visible ? (
            <EyeOff className="w-4 h-4" strokeWidth={1.5} />
          ) : (
            <Eye className="w-4 h-4" strokeWidth={1.5} />
          )}
        </button>
      </div>
      {hint && <div className="text-[11px] text-neutral-500 font-manrope mt-1.5">{hint}</div>}
    </div>
  );
}
